import { useState, useEffect, useRef } from "react";
import * as THREE from "three";

const textureLoader = new THREE.TextureLoader();

// Textura por defecto: esfera negra (lazy loading)
let _defaultTexture = null;
const getDefaultTexture = () => {
  if (_defaultTexture) return _defaultTexture;

  try {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");

    // Fondo negro
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, 256, 256);

    _defaultTexture = new THREE.CanvasTexture(canvas);
    _defaultTexture.needsUpdate = true;
  } catch (e) {
    // Fallback si no se puede crear canvas
    _defaultTexture = new THREE.Texture();
    _defaultTexture.needsUpdate = true;
  }

  return _defaultTexture;
};

/**
 * Hook para cargar texturas de forma segura con fallback
 * @param {string} url - URL de la textura a cargar
 * @param {function} onStatusChange - Callback para notificar cambios de estado { isLoading, hasError }
 * @returns {{ texture: THREE.Texture, isLoading: boolean, hasError: boolean }}
 */
export const useSafeTexture = (url, onStatusChange) => {
  const [texture, setTexture] = useState(() => getDefaultTexture());
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const onStatusChangeRef = useRef(onStatusChange);

  // Mantener ref actualizada sin causar re-renders
  onStatusChangeRef.current = onStatusChange;

  useEffect(() => {
    console.log("[useSafeTexture] URL:", url);

    // Notificar inmediatamente que ya no está cargando cuando no hay URL
    if (!url) {
      console.log("[useSafeTexture] No URL, using default texture");
      setTexture(getDefaultTexture());
      setIsLoading(false);
      setHasError(false);
      onStatusChangeRef.current?.({ isLoading: false, hasError: false });
      return;
    }

    // Comenzar a cargar
    console.log("[useSafeTexture] Starting to load:", url);
    setIsLoading(true);
    setHasError(false);
    onStatusChangeRef.current?.({ isLoading: true, hasError: false });

    let isMounted = true;

    textureLoader.load(
      url,
      (loadedTexture) => {
        if (isMounted) {
          console.log("[useSafeTexture] Texture loaded successfully");
          setTexture(loadedTexture);
          setIsLoading(false);
          setHasError(false);
          onStatusChangeRef.current?.({ isLoading: false, hasError: false });
        }
      },
      undefined,
      (error) => {
        if (isMounted) {
          console.warn("[useSafeTexture] Error loading texture:", url, error);
          setTexture(getDefaultTexture());
          setIsLoading(false);
          setHasError(true);
          onStatusChangeRef.current?.({ isLoading: false, hasError: true });
        }
      },
    );

    return () => {
      isMounted = false;
    };
  }, [url]);

  return {
    texture,
    isLoading,
    hasError,
  };
};

export default useSafeTexture;
